import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Edit2, Trash2, User } from 'lucide-react';
import { cn } from '../lib/utils';

const emptyForm = () => ({ name: '', address: '', email: '', phone: '', notes: '' });

export default function OwnersPage() {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const res = await api.get('/owners');
    setOwners(res.data);
  };

  useEffect(() => {
    load().catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = owners.filter(o => !normalizedSearch || [
    o.name, o.address, o.email, o.phone, o.notes,
  ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch)));

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (owner) => {
    setEditing(owner);
    setForm({
      name: owner.name || '',
      address: owner.address || '',
      email: owner.email || '',
      phone: owner.phone || '',
      notes: owner.notes || '',
    });
    setDlgOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name ist Pflicht'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/owners/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/owners', form);
        toast.success('Eigentümer erstellt');
      }
      setDlgOpen(false);
      await load();
    } catch {
      toast.error('Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await api.delete(`/owners/${id}`);
    toast.success('Gelöscht');
    await load();
    if (selected?.id === id) setSelected(null);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="owners-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Eigentümer</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{owners.length} Eigentümer</p>
          </div>
          <Button onClick={openNew} data-testid="new-owner-btn" className="rounded-sm gap-2">
            <Plus className="w-4 h-4" />Neuer Eigentümer
          </Button>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Eigentümer suchen..." className="pl-9 rounded-sm" data-testid="owner-search" />
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-40"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map(owner => (
                  <div
                    key={owner.id}
                    data-testid={`owner-row-${owner.id}`}
                    onClick={() => setSelected(owner)}
                    className={cn("flex items-center justify-between p-4 border rounded-sm cursor-pointer transition-all stagger-item",
                      selected?.id === owner.id ? "border-primary bg-primary/5" : "border-border/60 bg-card hover:border-primary/30 hover:shadow-sm"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground">{owner.name}</p>
                      {owner.email && <p className="text-xs text-muted-foreground truncate">{owner.email}</p>}
                      {owner.address && <p className="text-xs text-muted-foreground truncate">{owner.address}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <button onClick={e => { e.stopPropagation(); openEdit(owner); }} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-sm hover:bg-muted" title="Bearbeiten">
                        <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(owner.id); }} className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-sm hover:bg-destructive/10" data-testid={`delete-owner-${owner.id}`} title="Löschen">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Keine Eigentümer gefunden</p>}
              </div>
            )}
          </div>

          {selected && (
            <div className="w-72 shrink-0 space-y-4" data-testid="owner-detail-panel">
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-base">{selected.name}</h3>
                </div>
                {selected.address && <p className="text-sm text-muted-foreground mb-1">{selected.address}</p>}
                {selected.email && <p className="text-sm text-muted-foreground mb-1">{selected.email}</p>}
                {selected.phone && <p className="text-sm text-muted-foreground mb-1">{selected.phone}</p>}
                {selected.notes && <p className="text-sm text-muted-foreground mt-2 pt-2 border-t border-border/60 whitespace-pre-wrap">{selected.notes}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Eigentümer bearbeiten' : 'Neuer Eigentümer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="owner-form-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notizen</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="rounded-sm resize-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-owner-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
